/**
 * @class Accordion
 * @description A class for managing accordion components
 */
class Accordion {

  /**
   * @constructor
   * @param {HTMLElement} root - The root element of the accordion
   * @param {Object} options - The options for the accordion
   */
  constructor(root, { defaultOpen = -1 } = {}) {
    this.root = root;
    this.items = Array.from(root.querySelectorAll('[data-accordion-item]'));
    this.defaultOpen = defaultOpen;
  }


  /**
   * @method init
   * @description Initializes the accordion
   */
  init() {
    if (!this.root || this.items.length === 0) return;

    this.items.forEach((item, idx) => {
      const btn = item.querySelector('[data-accordion-button]');
      if (btn) btn.addEventListener('click', () => this.toggle(idx));
    });


    if (this.defaultOpen >= 0 && this.defaultOpen < this.items.length) {
      this.open(this.defaultOpen);
    }
  }


  /**
   * @method toggle
   * @description Toggles the accordion item
   * @param {number} index - The index of the item to toggle
   */
  toggle(index) {
    if (index < 0 || index >= this.items.length) return;

    this.items.forEach((item, idx) => {
      idx === index ? this.open(idx) : this.close(idx);
    });
  }


  /**
   * @method open
   * @description Opens the accordion item
   * @param {number} index - The index of the item to open
   */
  open(index) {
    const { content, icon } = this.#getElements(index);
    if (!content || !icon) return;
    content.classList.remove('tw-hidden');
    icon.classList.add('tw-rotate-180');
  }


  /**
   * @method close
   * @description Closes the accordion item
   * @param {number} index - The index of the item to close
   */
  close(index) {
    const { content, icon } = this.#getElements(index);
    if (!content || !icon) return;
    content.classList.add('tw-hidden');
    icon.classList.remove('tw-rotate-180');
  }


  /**
   * @method #getElements
   * @description Gets the elements of the accordion item
   * @param {number} index - The index of the item to get the elements
   * @returns {Object} The elements of the accordion item
   */
  #getElements(index) {
    const item = this.items[index];
    return {
      content: item?.querySelector('[data-accordion-content]'),
      icon:    item?.querySelector('[data-accordion-icon]')
    };
  }


  /**
   * @method static initAll
   * @description Initializes all the accordion components
   */
  static initAll() {
    document.querySelectorAll('[data-component="accordion"]').forEach(root => {
      const idxAttr = Number(root.dataset.cardOpenIndex);
      const defaultOpen = Number.isFinite(idxAttr) ? idxAttr : -1;
      new Accordion(root, { defaultOpen }).init();
    });
  }
}


/**
 * @event DOMContentLoaded
 * @description Initializes all the accordion components
 */
document.addEventListener('DOMContentLoaded', () => Accordion.initAll());